// auth.jsx — lightweight CLIENT-SIDE-ONLY access gate for KAIJEGO CRM.
//
// ⚠️  NOT real security. There is no server here: the shared password lives in the
//     frontend bundle, so anyone who opens dev-tools can read it, and the backend
//     API stays reachable directly. This only keeps casual users out of the UI.
//     For anything sensitive, verify credentials on the server instead.
//
// Rule: email must end in @kaijego.com AND password must equal the shared password.
import React, { useState, useCallback, useContext, createContext } from "react";
import { Btn, Inp, Icon } from "./ui";
import { I } from "./utils";
import { BRAND_HEX, BRAND_RING, BRAND_GRADIENT } from "./theme";
import { BRAND } from "./brand";

const AUTH_KEY        = "kaijego_auth";   // localStorage key
const SHARED_PASSWORD = "ReD#05.LanE#25";   // shared team password
// Allowed staff domain comes from brand.js (dots escaped for the regex).
const EMAIL_RE        = new RegExp(`^[^\\s@]+@${BRAND.emailDomain.replace(/\./g, "\\.")}$`, "i");

// eye-off path (utils I only ships a plain eye icon)
const EYE_OFF = "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21";

// Safe localStorage access (won't crash if storage is unavailable/blocked).
const store = {
  get()  { try { return localStorage.getItem(AUTH_KEY); } catch { return null; } },
  set(v) { try { localStorage.setItem(AUTH_KEY, v); } catch { /* ignore */ } },
  clear(){ try { localStorage.removeItem(AUTH_KEY); } catch { /* ignore */ } },
};

/** True only when the email is on the brand domain AND the password matches. */
export function validateCredentials(email, password) {
  const e = (email || "").trim().toLowerCase();
  return EMAIL_RE.test(e) && password === SHARED_PASSWORD;
}

// ─── Context ────────────────────────────────────────────────────────────────
const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  // `user` is the signed-in email string, or null when logged out.
  const [user, setUser] = useState(() => store.get());

  const login = useCallback((email) => {
    const e = (email || "").trim().toLowerCase();
    store.set(e);
    setUser(e);
  }, []);

  const logout = useCallback(() => {
    store.clear();
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

/** Renders children only when signed in; otherwise shows the login screen. */
export function RequireAuth({ children }) {
  const { user } = useAuth();
  return user ? children : <LoginScreen />;
}

// ─── Login screen ─────────────────────────────────────────────────────────────
function LoginScreen() {
  const { login } = useAuth();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [show, setShow]         = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (validateCredentials(email, password)) {
      setError("");
      login(email);
      return;
    }
    // Give a targeted hint without revealing the password.
    const okEmail = EMAIL_RE.test(email.trim().toLowerCase());
    setError(okEmail ? "Incorrect password." : `Use your @${BRAND.emailDomain} email address.`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <style>{`
        @keyframes modalIn { from { opacity: 0; transform: translateY(16px) scale(.97); } to { opacity: 1; transform: none; } }
      `}</style>

      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-slate-100 p-7"
        style={{ animation: "modalIn .25s ease", fontFamily: "var(--font-body)" }}
      >
        <div className="flex flex-col items-center text-center mb-6">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
            style={{ background: BRAND_GRADIENT }}
          >
            <Icon d={I.plane} size={20} className="text-white" />
          </div>
          <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: "var(--font-heading)" }}>
            {BRAND.appName}
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">Sign in to continue</p>
        </div>

        <div className="space-y-4">
          <Inp
            label="Email"
            type="email"
            placeholder={`you@${BRAND.emailDomain}`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
              Password
            </label>
            <div className="relative">
              <input
                type={show ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={(e) => { e.target.style.borderColor = BRAND_HEX; e.target.style.boxShadow = `0 0 0 3px ${BRAND_RING}`; }}
                onBlur={(e)  => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
                className="w-full px-3.5 py-2.5 pr-11 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none transition-all placeholder:text-slate-300"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                tabIndex={-1}
                aria-label={show ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <Icon d={show ? EYE_OFF : I.eye} size={16} />
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2">
              <Icon d={I.warn} size={15} /> {error}
            </div>
          )}

          <Btn type="submit" variant="primary" className="w-full justify-center">
            Sign In
          </Btn>
        </div>

        <p className="text-[11px] text-slate-400 text-center mt-5">
          Access restricted to {BRAND.emailDomain} team members.
        </p>
      </form>
    </div>
  );
}